import * as O from 'fp-ts/Option';
import { LayoutModularFurniture, LayoutModularFurnitureT } from './layout/modularFurniture';
import {
    getFurniture,
    getModularFurniture,
    LayoutFurniture,
    LayoutFurnitureT,
} from './layout/furniture';
import * as Arya from '../components/arya';

import { ConfigurableProperties, ModaSofa } from '@dora/backpack';
import * as R from './room';
import * as t from 'io-ts';

const Required = t.type({
    furniture: t.array(LayoutFurniture),
    modularFurniture: t.array(LayoutModularFurniture),
    rangeFilter: t.array(t.string),
    space: t.type({ room: R.SerialisedRoom }),
});

const Optional = t.partial({
    globalConfigurablePropertySelections: t.array(
        ConfigurableProperties.ConfigurablePropertySelection,
    ),
});

export const LayoutPostData = t.intersection([Required, Optional]);
export type LayoutPostData = t.TypeOf<typeof LayoutPostData>;

/**
 * A serialised representation of the current scene including but not limited to products
 * (non-modular as well as modular), the current range, and room dimensions.
 *
 * These layouts are used to save/load a user's progress and are serialised to the layout store
 * (haymaker).
 *
 * For layouts used to represent the arrangement of Moda's product sets, see `data/productSetLayout`
 */
export const Layout = t.intersection([LayoutPostData, t.type({ id: t.string })]);
export type LayoutT = t.TypeOf<typeof Layout>;

/**
 * Generate a Layout object from the current scene
 *
 * @param arya Used to fetch the current state (for position / rotation) from the 3d scene, as well
 * as the room dimensions
 * @param furnitureList Front-end representation of products in the scene
 * @param range The current range the app is being filtered to
 * @param room The current room dimensions
 */
export function generate(
    arya: O.Option<Arya.Interface>,
    furnitureList: Array<{ aryaId: string; configuration: O.Option<ModaSofa.Configuration> }>,
    range: string,
    room: R.Room,
    globalPropSelections: ConfigurableProperties.ConfigurablePropertySelectionT[] | null,
): LayoutPostData {
    const furniture: LayoutFurnitureT[] = getFurniture(furnitureList, arya);

    const modularFurniture: LayoutModularFurnitureT[] = getModularFurniture(furnitureList, arya);

    return {
        modularFurniture,
        furniture,
        rangeFilter: [range],
        space: { room: R.serialise(room) },
        globalConfigurablePropertySelections: globalPropSelections ?? undefined,
    };
}
